/**
 * The connect window (slice 3b spec §5.2; Paper 41, 41b) and Change access
 * (§5.3; Paper 44): the app's three levels, what each allows, and what
 * Google will show. A dialog on a desktop, a sheet from the bottom on a phone.
 *
 * Connect preselects the middle level and goes to Google (`POST …/connect`,
 * then the returned URL), in a tab of its own when the browser allows one. Change access preselects the account's level and
 * saves it: a setting, not a reconnect (§2), so it never signs in again.
 */
import { Button } from "@crew/ui/components/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@crew/ui/components/dialog";
import { RadioGroup, RadioGroupCard } from "@crew/ui/components/radio-group";
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "@crew/ui/components/drawer";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import { ArrowUpRight, ShieldCheck, TriangleAlert } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { errorHint } from "../../app/api";
import { AppIcon } from "./integrations-parts";
import type { Account, Level, LevelInfo, Provider } from "./integrations-api";
import { useChangeAccount, useConnect } from "./integrations-api";
import { accessChange } from "./integrations-view";
import { openSignInTab } from "./signin-tab";

/** What the window needs of an app: from Integrations' card or Browse apps' tile. */
export type WindowApp = {
  app: string;
  name: string;
  icon?: string;
  mark?: string;
  consent: string;
  provider: Provider;
  levels: LevelInfo[];
};

export type WindowMode = { kind: "connect" } | { kind: "access"; account: Account };

/** The middle level is Recommended when connecting; Change access marks the account's own "Now". */
function tagFor(level: Level, current: Level | undefined): string | null {
  if (current) return level === current ? "Now" : null;
  return level === "write" ? "Recommended" : null;
}

function LevelCard({
  info,
  checked,
  tag,
}: {
  info: LevelInfo;
  checked: boolean;
  tag: string | null;
}) {
  return (
    <RadioGroupCard
      value={info.level}
      data-level={info.level}
      className={cn(
        "flex items-start gap-3 rounded-lg px-4 py-3.5 text-left",
        checked
          ? "bg-foreground/[0.024] shadow-[0_0_0_1.5px_var(--color-foreground)]"
          : "bg-card shadow-[0_0_0_1px_rgb(31_42_68/0.14)]",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "mt-px flex size-4.5 shrink-0 items-center justify-center rounded-full",
          checked ? "bg-foreground" : "shadow-[inset_0_0_0_1.5px_rgb(31_42_68/0.28)]",
        )}
      >
        {checked ? <span className="size-1.5 rounded-full bg-white" /> : null}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.75">
        <span className="flex items-center gap-2">
          <span className="text-body font-semibold text-foreground">{info.name}</span>
          {tag ? (
            <span
              className={cn(
                "text-xs font-medium",
                tag === "Recommended" ? "text-moss" : "text-muted-foreground",
              )}
            >
              {tag}
            </span>
          ) : null}
        </span>
        <span
          className={cn(
            "text-meta leading-[19px]",
            checked ? "text-copy" : "text-muted-foreground",
          )}
        >
          {info.allows}
        </span>
      </span>
    </RadioGroupCard>
  );
}

/** The levels as cards, one chosen. */
function LevelPicker({
  levels,
  value,
  onChange,
  current,
}: {
  levels: LevelInfo[];
  value: Level;
  onChange: (level: Level) => void;
  /** The account's level, when changing it. */
  current?: Level;
}) {
  return (
    <RadioGroup
      aria-label="Access"
      value={value}
      onValueChange={(v) => onChange(v as Level)}
      className="gap-2.5"
    >
      {levels.map((l) => (
        <LevelCard
          key={l.level}
          info={l}
          checked={l.level === value}
          tag={tagFor(l.level, current)}
        />
      ))}
    </RadioGroup>
  );
}

/** The window's body and footer, apart from its frame so a test can render it. */
export function ConnectBody({
  app,
  mode,
  level,
  onLevel,
  error,
  footer,
}: {
  app: WindowApp;
  mode: WindowMode;
  level: Level;
  onLevel: (level: Level) => void;
  error: unknown;
  footer: ReactNode;
}) {
  const account = mode.kind === "access" ? mode.account : null;
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-2.5 px-5 pb-5 md:px-6">
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {errorHint(error)}
          </p>
        ) : null}
        <LevelPicker
          levels={app.levels}
          value={level}
          onChange={onLevel}
          current={account?.level}
        />
        {account ? null : (
          // What Google will show, at every level (spec §2).
          <p className="flex items-start gap-1.5 pt-1 text-meta leading-[18px] font-medium text-copper">
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
            {app.consent}
          </p>
        )}
      </div>
      <p className="flex items-start gap-2 px-5 pb-5 text-meta leading-[19px] text-muted-foreground md:px-6">
        <ShieldCheck className="mt-0.5 size-3.5 shrink-0" />
        Within this level your crew acts without asking first, and every call is on Activity. You
        can change it any time.
      </p>
      {footer}
    </div>
  );
}

function windowWords(app: WindowApp, mode: WindowMode) {
  if (mode.kind === "access") {
    const a = mode.account;
    return {
      title: `Change access for ${a.label ?? a.email ?? app.name}`,
      line: `${a.name && a.email ? `${a.email}. ` : ""}It changes at once, with no new sign-in.${a.primary ? " It stays primary." : ""}`,
    };
  }
  return { title: `Connect ${app.name}`, line: "How much can your crew do in this account?" };
}

/**
 * The window. `open` with an app shows it; closing (Cancel, ×, the scrim)
 * calls `onClose`. A connect sent to its own tab closes it; one that leaves
 * this page for Google (the tab was blocked) keeps it busy.
 */
export function ConnectWindow({
  app,
  mode,
  onClose,
  onSaved,
  onSignInOpened,
}: {
  app: WindowApp | null;
  mode: WindowMode;
  onClose: () => void;
  /** After Change access saves: the page's notice. */
  onSaved?: (message: string) => void;
  /** A connect's sign-in went to its own tab (the window has closed). */
  onSignInOpened?: () => void;
}) {
  const mobile = useIsMobile();
  // While a request runs, nothing the person does closes the window: a connect
  // is about to leave for Google, and a save's notice comes when it lands. A
  // saved change closes the window itself (Devin on #247).
  const [locked, setLocked] = useState(false);
  const open = app !== null;
  const close = () => {
    if (!locked) onClose();
  };
  const content = app ? (
    <Window
      app={app}
      mode={mode}
      onClose={close}
      onSaved={(message) => {
        onSaved?.(message);
        onClose();
      }}
      onDone={() => {
        onClose();
        onSignInOpened?.();
      }}
      onBusy={setLocked}
    />
  ) : null;
  if (mobile) {
    return (
      <Drawer open={open} onOpenChange={(o) => !o && close()} showSwipeHandle>
        <DrawerContent
          className="data-[swipe-direction=down]:rounded-t-2xl"
          showCloseButton={!locked}
        >
          {content}
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="gap-0 p-0 sm:max-w-[580px]" showCloseButton={!locked}>
        {content}
      </DialogContent>
    </Dialog>
  );
}

/** Connect goes to Google; Change access saves the level and closes. */
function useSubmit(
  app: WindowApp,
  account: Account | null,
  onClose: () => void,
  /** A saved change's notice; it closes the window, past the lock. */
  onSaved: (message: string) => void,
  /** A sign-in sent to its own tab: the window closes, past the lock. */
  onDone: () => void,
) {
  const connect = useConnect();
  const change = useChangeAccount();
  const submit = (level: Level) => {
    if (!account) {
      const started = { app: app.app, name: app.name, provider: app.provider.name };
      // The sign-in's tab opens now, in the click, so no popup blocker stops it.
      const tab = openSignInTab(undefined, started);
      // In its own tab, this window closes and the page shows the row waiting on it.
      connect.mutate(
        { app: app.app, level, started, tab },
        { onSuccess: (out) => out.inTab && onDone() },
      );
      return;
    }
    const body = accessChange(account, level);
    if (!body) return onClose();
    const name = app.levels.find((l) => l.level === level)?.name ?? level;
    change.mutate(
      { id: account.id, ...body },
      {
        onSuccess: () => {
          onSaved(`${account.label ?? app.name} is now ${name}.`);
        },
      },
    );
  };
  return {
    submit,
    busy: connect.isPending || change.isPending,
    error: connect.error ?? change.error,
  };
}

/** Cancel and the window's action: side by side on a desktop, stacked full width on a phone. */
function WindowFooter({
  mobile,
  access,
  provider,
  primary,
  busy,
  onClose,
}: {
  mobile: boolean;
  access: boolean;
  /** Who the sign-in is with: "Google". */
  provider: string;
  primary: ReactNode;
  busy: boolean;
  onClose: () => void;
}) {
  if (mobile) {
    return (
      <div className="flex flex-col gap-1 border-t border-border px-4 pt-4 pb-6">
        <div className="flex flex-col [&>button]:h-11 [&>button]:w-full">{primary}</div>
        <Button variant="ghost" size="lg" className="h-11" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 rounded-b-xl border-t border-border bg-foreground/[0.02] px-6 py-3.5">
      <span className="flex-1 text-meta text-muted-foreground">
        {access
          ? "Your crew's next call is checked at the new level."
          : `You'll sign in with ${provider} in a new tab.`}
      </span>
      <Button variant="ghost" size="lg" onClick={onClose} disabled={busy}>
        Cancel
      </Button>
      {primary}
    </div>
  );
}

function Window({
  app,
  mode,
  onClose,
  onSaved,
  onDone,
  onBusy,
}: {
  app: WindowApp;
  mode: WindowMode;
  onClose: () => void;
  onSaved: (message: string) => void;
  onDone: () => void;
  onBusy: (busy: boolean) => void;
}) {
  const mobile = useIsMobile();
  const account = mode.kind === "access" ? mode.account : null;
  const [level, setLevel] = useState<Level>(account?.level ?? "write");
  const { submit, busy, error } = useSubmit(app, account, onClose, onSaved, onDone);
  useEffect(() => {
    onBusy(busy);
    return () => onBusy(false);
  }, [busy, onBusy]);
  const { title, line } = windowWords(app, mode);
  const primary = account ? (
    <Button size="lg" onClick={() => submit(level)} disabled={busy || level === account.level}>
      Save
    </Button>
  ) : (
    <Button size="lg" onClick={() => submit(level)} disabled={busy} className="gap-2 px-4">
      Continue to {app.provider.name}
      <ArrowUpRight className="size-3" />
    </Button>
  );
  const TitleEl = mobile ? DrawerTitle : DialogTitle;
  const LineEl = mobile ? DrawerDescription : DialogDescription;
  return (
    <div data-connect-window={app.app}>
      <div className="flex items-start gap-3.5 px-5 pt-5 pb-4.5 md:px-6 md:pt-6">
        <span className="hidden md:block">
          <AppIcon app={app.app} icon={app.icon} mark={app.mark} size="lg" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1 pr-6">
          <TitleEl className="text-[20px] leading-[26px] font-semibold tracking-tight text-foreground">
            {title}
          </TitleEl>
          <LineEl className="text-sm text-muted-foreground">{line}</LineEl>
        </div>
      </div>
      <ConnectBody
        app={app}
        mode={mode}
        level={level}
        onLevel={setLevel}
        error={error}
        footer={
          <WindowFooter
            mobile={mobile}
            access={account !== null}
            provider={app.provider.name}
            primary={primary}
            busy={busy}
            onClose={onClose}
          />
        }
      />
    </div>
  );
}
